import React, { useState, useCallback, useMemo } from 'react';
import { useRouteMatch } from 'react-router';

import {
  Button,
  Copy,
  DynamicTopPanel,
  Input,
  JsonFormBuilder,
  Loading,
} from '..';
import { PERMISSIONS } from '../../shared/constants/permissions';
import { usePermissionAllowed } from '../../hooks';
import type { FormJsonData, AgentWidgetSettings } from '../../api';

type FormBuilderModalProps = {
  onChange: (data: any) => void;
  widgetSettings: AgentWidgetSettings | null;
  schemaData: FormJsonData;
  intializing: boolean;
  onSave?: () => void;
  showSaveButton?: boolean;
  handleFileUpload?: (file: Blob) => Promise<string | undefined>;
  options?: any;
};

export const WidgetSettingsBuilder: React.FC<FormBuilderModalProps> = ({
  widgetSettings,
  onChange,
  schemaData,
  intializing,
  onSave,
  showSaveButton = false,
  handleFileUpload,
  options,
}) => {
  const { params } = useRouteMatch<{ organization_id: string }>();
  const isAllowedAgentEdit = usePermissionAllowed(PERMISSIONS.EDIT_AGENTS);

  const [isError, setIsError] = useState(false);

  const handleChanges = useCallback(
    ({ data, errors }: { data: any; errors: any }) => {
      const isLocal = process.env.NODE_ENV === 'development';
      const isDev =
        process.env.REACT_APP_ENV === 'dev' ||
        (typeof window !== 'undefined' &&
          window.location.hostname.includes('dev-admin'));

      const shouldLog = isLocal || isDev;

      if (shouldLog) {
        console.log('WidgetSettingsBuilder - data:', data);
        console.log('WidgetSettingsBuilder - errors:', errors);
      }

      onChange(data);
      setIsError(!!errors.length);
    },
    [onChange]
  );

  const widgetSettingsJson = useMemo(
    () => JSON.stringify(widgetSettings, null, 2) || '',
    [widgetSettings]
  );

  return (
    <>
      {intializing && !widgetSettings ? (
        <Loading />
      ) : (
        <div>
          <div className="px-4">
            <div className="flex justify-end gap-x-md mb-2">
              <div className="text-small truncate">Copy Widget Settings</div>
              <Copy className="shrink-0" text={widgetSettingsJson} />
            </div>
            <Input value={widgetSettingsJson} multiline readonly />
          </div>
          <JsonFormBuilder
            onChange={handleChanges}
            formData={widgetSettings || {}}
            schemaData={schemaData}
            onChangeUpload={handleFileUpload}
            options={options}
          />
        </div>
      )}

      {showSaveButton && isAllowedAgentEdit && (
        <DynamicTopPanel>
          <div className="flex flex-row gap-x-md justify-center items-center">
            <Button
              label="Cancel"
              url={`/agents/${params.organization_id}`}
              type="contrast"
              size="medium"
            />
            <Button
              label={'Save Widget Settings'}
              size="medium"
              type="primary"
              disabled={intializing || isError}
              onClick={onSave}
            />
          </div>
        </DynamicTopPanel>
      )}
    </>
  );
};
